/**
 * Report page — full accessibility report for a website.
 *
 * Shows score, truthfulness/coverage stats, issue groups (with screenshots),
 * per-template breakdown, and data quality warnings.
 */

import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useLatestReport, useReportPayload } from "../hooks/use-report.js";
import { usePostScanRefresh } from "../hooks/use-post-scan-refresh.js";
import { ScoreCard } from "../components/score-card.js";
import { WarningPanel } from "../components/warning-panel.js";
import { ReportIssueGroupList } from "../components/issue-group-list.js";
import { useFirstOrg } from "../hooks/use-orgs.js";
import { useWebsites } from "../hooks/use-websites.js";
import { useBillingSummary } from "../hooks/use-billing.js";
import { Breadcrumbs } from "../components/breadcrumbs.js";
import { PageSkeleton } from "../components/skeleton.js";
import type { PageDiagnosticStatus, ReportScannerIdentity, ReportPageDiagnostic, ReportIssueGroup } from "../api/product-types.js";

/* ── Helpers ────────────────────────────────────────────────────────────────── */

function statusLabel(status: PageDiagnosticStatus): string {
  switch (status) {
    case "scanned":
      return "Scanned";
    case "blocked":
      return "Blocked";
    case "failed":
      return "Failed";
    case "not_selected":
      return "Not selected";
  }
}

function truncateUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname + u.search;
    return path.length > 60 ? path.slice(0, 57) + "…" : path || "/";
  } catch {
    return url.length > 60 ? url.slice(0, 57) + "…" : url;
  }
}

function buildSupportText(
  domain: string,
  blockedPages: ReportPageDiagnostic[],
  failedPages: ReportPageDiagnostic[],
  identity: ReportScannerIdentity,
): string {
  const lines: string[] = [];
  lines.push(`AccessiMonitor scan access request for ${domain}`);
  lines.push("");
  lines.push(
    "Our automated accessibility scanner was unable to access some pages on your site.",
  );
  lines.push(
    "The scanner uses a headless Chromium browser with Axe-core, PA11y, and custom checks.",
  );
  lines.push("");
  lines.push("Scanner identification:");
  lines.push(`  User-Agent: ${identity.userAgent}`);
  for (const [k, v] of Object.entries(identity.customHeaders)) {
    lines.push(`  Header: ${k}: ${v}`);
  }
  if (!identity.fixedIpKnown) {
    lines.push(
      "  IP address: No fixed scanner IP is configured. Contact AccessiMonitor support if IP-based allowlisting is required.",
    );
  }
  if (blockedPages.length > 0) {
    lines.push("");
    lines.push(`Blocked pages (${blockedPages.length}):`);
    for (const p of blockedPages.slice(0, 20)) {
      lines.push(`  ${p.url} — ${p.reasonLabel}`);
    }
    if (blockedPages.length > 20) {
      lines.push(`  ... and ${blockedPages.length - 20} more`);
    }
  }
  if (failedPages.length > 0) {
    lines.push("");
    lines.push(`Pages that failed to load (${failedPages.length}):`);
    for (const p of failedPages.slice(0, 10)) {
      lines.push(`  ${p.url} — ${p.reasonLabel}`);
    }
    if (failedPages.length > 10) {
      lines.push(`  ... and ${failedPages.length - 10} more`);
    }
  }
  lines.push("");
  lines.push(
    "Please allow automated browser access for the URLs listed above.",
  );
  lines.push(
    "If your site uses Cloudflare, a WAF, or bot protection, you can allowlist " +
      "requests matching the User-Agent or custom headers shown above.",
  );
  return lines.join("\n");
}

/* ── CSV export helpers ──────────────────────────────────────────────────── */

function csvEscape(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return '"' + value.replace(/"/g, '""') + '"';
  }
  return value;
}

function generateIssueCSV(groups: ReportIssueGroup[], _websiteDomain: string): string {
  const BOM = "\uFEFF";
  const headers = ["Severity", "WCAG Criterion", "WCAG Level", "Title", "Affected Pages", "Source Engines", "Description"];
  const rows = groups.map((g) => [
    csvEscape(g.severity),
    csvEscape(g.criterion ?? ""),
    csvEscape(g.wcagLevel ?? ""),
    csvEscape(g.title),
    String(g.affectedPagesCount),
    csvEscape(g.sourceEngines.join(", ")),
    csvEscape(g.description),
  ]);
  return BOM + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
}

function generatePageDiagnosticsCSV(diagnostics: ReportPageDiagnostic[]): string {
  const BOM = "\uFEFF";
  const headers = ["URL", "Status", "Reason", "Template"];
  const rows = diagnostics.map((p) => [
    csvEscape(p.url),
    csvEscape(p.status),
    csvEscape(p.reasonLabel),
    csvEscape(p.templateLabel ?? ""),
  ]);
  return BOM + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
}

function downloadCSV(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function CopyBlock({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Clipboard API may be unavailable in HTTP contexts
    });
  }

  return (
    <div className="support-block">
      <div className="support-block__header">
        <span className="text-sm fw-600">
          Send this to your developer or hosting provider
        </span>
        <button
          type="button"
          className="btn btn--sm btn--secondary"
          onClick={handleCopy}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="support-block__pre">{text}</pre>
    </div>
  );
}

export function ReportPage() {
  const { websiteId } = useParams<{ websiteId: string }>();

  const { data: summary, isLoading: summaryLoading, isError: summaryError } =
    useLatestReport(websiteId);
  const { data: payload, isLoading: payloadLoading } = useReportPayload(
    websiteId,
    summary?.id,
  );

  const org = useFirstOrg();
  const { data: websitesData } = useWebsites(org?.id);
  const website =
    websitesData?.websites.find((w) => w.id === websiteId) ?? null;
  const { data: billingData } = useBillingSummary(org?.id);
  const screenshotsEnabled = billingData?.summary?.limits?.screenshotsEnabled ?? false;

  const { execution } = usePostScanRefresh(websiteId, org?.id);

  // Detect stale report: latest pipeline failed/partial and this report
  // predates it.
  const isReportStale =
    summary != null &&
    execution != null &&
    execution.status !== "running" &&
    (execution.status === "failed" || execution.status === "partial") &&
    summary.startedAt < (execution.startedAt ?? "");

  const loading = summaryLoading || (summary != null && payloadLoading);

  if (loading) return <PageSkeleton />;

  if (!summary || !payload) {
    return (
      <div className="product-page">
        <Breadcrumbs
          items={[
            { label: "Websites", to: "/websites" },
            { label: website?.domain ?? "Website", to: `/websites/${websiteId}` },
            { label: "Report" },
          ]}
        />
        <h1 className="page-title">Report</h1>
        {summaryError ? (
          <div className="alert alert--error" role="alert">
            Failed to load report data. Try refreshing the page.
          </div>
        ) : (
        <p className="text-muted">
          No report has been generated yet. Run a scan to produce a report.
        </p>
        )}
        {!summaryError && (
        <Link
          to={`/websites/${websiteId}/scan`}
          className="btn btn--primary mt-2"
        >
          Run a scan
        </Link>
        )}
      </div>
    );
  }

  const { score, truthfulness, issueGroups, templates, pageDiagnostics, scannerIdentity, warnings } = payload;

  const blockedPages = pageDiagnostics.filter((p) => p.status === "blocked");
  const failedPages = pageDiagnostics.filter((p) => p.status === "failed");
  const hasAccessIssues = blockedPages.length > 0 || failedPages.length > 0;

  return (
    <div className="product-page">
      <Breadcrumbs
        items={[
          { label: "Websites", to: "/websites" },
          { label: website?.domain ?? "Website", to: `/websites/${websiteId}` },
          { label: "Report" },
        ]}
      />
      <h1 className="page-title">Accessibility report</h1>
      <p className="page-subtitle">
        Generated {new Date(payload.generatedAt).toLocaleString()} for{" "}
        <a href={payload.websiteUrl} target="_blank" rel="noopener noreferrer">
          {payload.websiteDomain}
        </a>
      </p>

      {isReportStale && (
        <div className="alert alert--warning mt-2" role="status">
          Your latest scan did not complete successfully. This report may be
          from a previous scan.
        </div>
      )}

      {/* ── Zero-coverage failure banner ─────────────────────────────────── */}
      {truthfulness.pagesScanned === 0 && truthfulness.pagesDiscovered > 0 && (
        <div className="alert alert--error mb-3" role="alert">
          <strong>This scan could not analyse any pages.</strong>{" "}
          {truthfulness.pagesDiscovered} {truthfulness.pagesDiscovered === 1 ? "page was" : "pages were"} discovered but none could
          be loaded. Your site may be blocking automated access, or pages may
          have failed to respond. Check the coverage details below and your{" "}
          <Link to={`/websites/${websiteId}/settings`}>
            scanner allowlisting settings
          </Link>.
        </div>
      )}

      {/* ── Score ──────────────────────────────────────────────────────────── */}
      <section className="product-section" aria-labelledby="report-score-h">
        <h2 id="report-score-h" className="section-title">
          Score
        </h2>
        <ScoreCard
          scoreValue={score.scoreValue}
          scoreBand={score.scoreBand}
          confidence={score.confidence}
          pagesScanned={truthfulness.pagesScanned}
        />
        {truthfulness.pagesScanned === 0 && (
          <div className="alert alert--warning mt-2" role="status">
            No pages were successfully scanned, so no score can be calculated.
            This does not mean your site has no accessibility issues.
          </div>
        )}
        {truthfulness.pagesScanned > 0 && score.confidence === "low" && (
          <p className="text-muted text-sm mt-2">
            Score confidence is low — too few pages were scanned to give
            a reliable picture. Run a deeper scan for a more accurate score.
          </p>
        )}
        {truthfulness.pagesScanned > 0 && (
          <div className="stat-grid mt-3">
            <div className="stat-item stat-item--danger">
              <span className="stat-item__value">{score.criticalCount}</span>
              <span className="stat-item__label">Critical</span>
            </div>
            <div className="stat-item stat-item--warning">
              <span className="stat-item__value">{score.seriousCount}</span>
              <span className="stat-item__label">Serious</span>
            </div>
            <div className="stat-item">
              <span className="stat-item__value">{score.moderateCount}</span>
              <span className="stat-item__label">Moderate</span>
            </div>
            <div className="stat-item">
              <span className="stat-item__value">{score.minorCount}</span>
              <span className="stat-item__label">Minor</span>
            </div>
          </div>
        )}
      </section>

      {/* ── Truthfulness / coverage ────────────────────────────────────────── */}
      <section className="product-section" aria-labelledby="coverage-h">
        <h2 id="coverage-h" className="section-title">
          Scan coverage
        </h2>
        <div className="stat-grid">
          <div className="stat-item">
            <span className="stat-item__value">
              {truthfulness.pagesScanned}
              <span className="text-muted text-sm fw-normal">
                {" "}/ {truthfulness.pagesDiscovered}
              </span>
            </span>
            <span className="stat-item__label">Pages scanned</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {truthfulness.coveragePercent}%
            </span>
            <span className="stat-item__label">Coverage</span>
          </div>
          {truthfulness.pagesBlocked > 0 && (
            <div className="stat-item stat-item--warning">
              <span className="stat-item__value">
                {truthfulness.pagesBlocked}
              </span>
              <span className="stat-item__label">Blocked by site security</span>
            </div>
          )}
          {truthfulness.pagesFailed > 0 && (
            <div className="stat-item stat-item--danger">
              <span className="stat-item__value">
                {truthfulness.pagesFailed}
              </span>
              <span className="stat-item__label">Failed to load</span>
            </div>
          )}
        </div>

        {/* Coverage explanation — always shown when partial */}
        {truthfulness.coverageExplanation && (
          <p className="text-muted text-sm mt-2">
            {truthfulness.coverageExplanation}
          </p>
        )}

        {truthfulness.coveragePercent < 50 &&
          truthfulness.pagesDiscovered > 0 && (
          <div className="alert alert--warning mt-2" role="status">
            Only {truthfulness.coveragePercent}% of discovered pages were
            scanned. Results may not represent the full site.
          </div>
        )}
        <p className="alert alert--info mt-2">
          {truthfulness.automatedTestingLimitations}
        </p>
        {truthfulness.missingDataWarnings.length > 0 && (
          <WarningPanel
            warnings={truthfulness.missingDataWarnings}
            label="Data quality notices"
          />
        )}
      </section>

      {/* ── What limited this scan ─────────────────────────────────────────── */}
      {truthfulness.pagesScanned < truthfulness.pagesDiscovered &&
        truthfulness.pagesDiscovered > 0 && (
        <section className="product-section" aria-labelledby="diagnostics-h">
          <h2 id="diagnostics-h" className="section-title">
            What limited this scan
          </h2>

          <ul className="diagnostics-list">
            {truthfulness.pageCapApplied && truthfulness.pageCapLimit > 0 && (
              <li className="diagnostics-list__item">
                <strong>Plan cap of {truthfulness.pageCapLimit} pages applied</strong>
                {" "}&mdash; your plan limits each scan to {truthfulness.pageCapLimit}{" "}
                representative pages. Upgrade your plan to scan more pages per run.
              </li>
            )}
            {truthfulness.pagesNotAttempted > 0 && (
              <li className="diagnostics-list__item">
                <strong>{truthfulness.pagesNotAttempted} pages were not tested</strong>
                {" "}because the scanner selects representative pages from each
                template group rather than scanning every discovered URL. This is
                expected — it provides broad coverage efficiently.
              </li>
            )}
            {truthfulness.pagesBlocked > 0 && (
              <li className="diagnostics-list__item diagnostics-list__item--warning">
                <strong>{truthfulness.pagesBlocked} pages were blocked</strong>
                {" "}by {truthfulness.wafVendorDetected === "Cloudflare"
                  ? "Cloudflare's security challenge"
                  : "the site's security protection (e.g. WAF, bot challenge, or login wall)"}.
                {" "}The scanner could not access these pages.
              </li>
            )}
            {truthfulness.pagesFailed > 0 && (
              <li className="diagnostics-list__item diagnostics-list__item--danger">
                <strong>{truthfulness.pagesFailed} pages failed to load</strong>
                {" "}&mdash; they returned errors, timed out, or had empty content.
                These pages may have temporary issues or may require JavaScript
                that the scanner could not execute.
              </li>
            )}
          </ul>

          {truthfulness.pagesBlocked > 0 && (
            <p className="text-muted text-sm mt-2">
              {truthfulness.wafVendorDetected === "Cloudflare" ? (
                <>
                  Cloudflare is blocking the scanner. You can fix this by adding
                  a WAF exception in your Cloudflare dashboard.{" "}
                  <Link to={`/websites/${websiteId}/settings`}>
                    View allowlisting instructions
                  </Link>.
                </>
              ) : (
                <>
                  See the troubleshooting section below for allowlisting guidance
                  you can share with your developer or hosting provider.
                </>
              )}
            </p>
          )}

          {truthfulness.pagesNotAttempted > 0 &&
            truthfulness.pagesBlocked === 0 &&
            truthfulness.pagesFailed === 0 && (
            <p className="text-muted text-sm mt-2">
              This is typical for most websites. The scanner tests a
              representative sample from each page template to give you broad
              coverage without redundant checks.
            </p>
          )}
        </section>
      )}

      {/* ── Troubleshooting blocked pages ──────────────────────────────────── */}
      {hasAccessIssues && (
        <section className="product-section" aria-labelledby="troubleshoot-h">
          <h2 id="troubleshoot-h" className="section-title">
            Troubleshooting blocked pages
          </h2>

          <p className="text-sm mb-3">
            The scanner uses an automated headless Chromium browser to check
            your site for accessibility issues. Some pages could not be accessed
            because the site's security protection blocked the scanner.
          </p>

          {blockedPages.length > 0 && (
            <div className="mb-3">
              <p className="text-sm mb-1">
                <strong>Likely causes:</strong>
              </p>
              <ul className="text-sm" style={{ margin: 0, paddingLeft: "1.25rem" }}>
                {blockedPages.some((p) => p.reasonCode === "CHALLENGE_PAGE") && (
                  <li>Bot challenge / security verification page (e.g. Cloudflare)</li>
                )}
                {blockedPages.some((p) => p.reasonCode === "WAF_BLOCK") && (
                  <li>Web application firewall (WAF) blocking automated requests</li>
                )}
                {blockedPages.some((p) => p.reasonCode === "LOGIN_WALL") && (
                  <li>Login-protected pages that require authentication</li>
                )}
              </ul>
            </div>
          )}

          {failedPages.length > 0 && blockedPages.length === 0 && (
            <div className="mb-3">
              <p className="text-sm mb-1">
                <strong>Likely causes:</strong>
              </p>
              <ul className="text-sm" style={{ margin: 0, paddingLeft: "1.25rem" }}>
                {failedPages.some((p) => p.reasonCode === "NAVIGATION_TIMEOUT" || p.reasonCode === "RENDER_TIMEOUT") && (
                  <li>Pages taking too long to load (timeout)</li>
                )}
                {failedPages.some((p) => p.reasonCode === "EMPTY_CONTENT") && (
                  <li>Pages returning empty or minimal content</li>
                )}
                {failedPages.some((p) => p.reasonCode === "JS_ERROR") && (
                  <li>JavaScript errors preventing the page from rendering</li>
                )}
              </ul>
            </div>
          )}

          <details className="mb-3">
            <summary className="text-sm cursor-pointer fw-600">
              Scanner identity details
            </summary>
            <div className="mt-2">
              <table className="page-diag-table" style={{ maxWidth: "600px" }}>
                <tbody>
                  <tr>
                    <td className="text-sm fw-600">User-Agent</td>
                    <td className="text-sm" style={{ wordBreak: "break-all" }}>
                      {scannerIdentity.userAgent}
                    </td>
                  </tr>
                  {Object.entries(scannerIdentity.customHeaders).map(([k, v]) => (
                    <tr key={k}>
                      <td className="text-sm fw-600">
                        Header: {k}
                      </td>
                      <td className="text-sm">{v}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="text-sm fw-600">Fixed IP</td>
                    <td className="text-sm text-muted">
                      {scannerIdentity.fixedIpKnown
                        ? "Yes (see below)"
                        : "Not available — contact support if IP-based allowlisting is required"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>

          <CopyBlock
            text={buildSupportText(
              payload.websiteDomain,
              blockedPages,
              failedPages,
              scannerIdentity,
            )}
          />
        </section>
      )}

      {/* ── Per-page scan diagnostics ──────────────────────────────────────── */}
      {pageDiagnostics.length > 0 && (
        <section className="product-section" aria-labelledby="page-diag-h">
          <details>
            <summary id="page-diag-h" className="section-title cursor-pointer">
              Page-by-page scan results ({pageDiagnostics.length} pages)
            </summary>
            <div className="mt-3">
              <div className="d-flex items-center flex-wrap gap-1" style={{ marginBottom: "0.75rem" }}>
                <p className="text-muted text-sm" style={{ margin: 0 }}>
                  These are the pages the scanner attempted to access. Pages
                  marked "Not selected" were discovered but not chosen for
                  scanning because only representative pages from each template
                  group are tested.
                </p>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => {
                    const csv = generatePageDiagnosticsCSV(pageDiagnostics);
                    downloadCSV(csv, `page-diagnostics-${website?.domain ?? "report"}-${new Date().toISOString().slice(0, 10)}.csv`);
                  }}
                  disabled={!pageDiagnostics.length}
                >
                  Export CSV
                </button>
              </div>
              <div className="page-diag-table-wrap">
                <table className="page-diag-table">
                  <thead>
                    <tr>
                      <th>URL</th>
                      <th>Status</th>
                      <th>Reason</th>
                      <th>Template</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageDiagnostics.map((p, i) => (
                      <tr key={i} className={`page-diag-table__row page-diag-table__row--${p.status}`}>
                        <td className="page-diag-table__url">
                          <a
                            href={p.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={p.url}
                          >
                            {truncateUrl(p.url)}
                          </a>
                        </td>
                        <td>
                          <span className={`page-diag-badge page-diag-badge--${p.status}`}>
                            {statusLabel(p.status)}
                          </span>
                        </td>
                        <td className="text-sm">{p.reasonLabel}</td>
                        <td className="text-sm text-muted">
                          {p.templateLabel ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </details>
        </section>
      )}

      {/* ── Issue groups ───────────────────────────────────────────────────── */}
      <section className="product-section" aria-labelledby="issues-h">
        <div className="d-flex items-center flex-wrap gap-1">
          <h2 id="issues-h" className="section-title" style={{ margin: 0 }}>
            Issue groups ({issueGroups.length})
          </h2>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => {
              const csv = generateIssueCSV(issueGroups, website?.domain ?? "report");
              downloadCSV(csv, `accessibility-issues-${website?.domain ?? "report"}-${new Date().toISOString().slice(0, 10)}.csv`);
            }}
            disabled={!issueGroups.length}
          >
            Export CSV
          </button>
        </div>
        <ReportIssueGroupList
          groups={issueGroups}
          pagesScanned={truthfulness.pagesScanned}
          pagesDiscovered={truthfulness.pagesDiscovered}
          screenshotsEnabled={screenshotsEnabled}
        />
      </section>

      {/* ── Templates ──────────────────────────────────────────────────────── */}
      {templates.length > 0 && (
        <section className="product-section" aria-labelledby="templates-h">
          <h2 id="templates-h" className="section-title">
            Templates ({templates.length})
          </h2>
          <ul className="template-list">
            {templates.map((t) => (
              <li key={t.templateId} className="template-row">
                <div className="template-row__head">
                  <span className="template-row__label">{t.label}</span>
                  <span
                    className={`quality-band quality-band--${t.qualityBand}`}
                  >
                    {t.qualityBand}
                  </span>
                  <span className="text-muted text-sm">
                    {t.memberCount} pages
                  </span>
                </div>
                <div className="template-row__counts text-sm">
                  <span className="text-danger">
                    {t.criticalCount} critical
                  </span>
                  <span>{t.seriousCount} serious</span>
                  <span>{t.moderateCount} moderate</span>
                  <span>{t.minorCount} minor</span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Warnings ───────────────────────────────────────────────────────── */}
      {warnings.length > 0 && (
        <section className="product-section">
          <WarningPanel warnings={warnings} label="Report warnings" />
        </section>
      )}

      {/* ── Related pages ────────────────────────────────────────────────── */}
      <section className="product-section">
        <div className="action-links">
          <Link
            to={`/websites/${websiteId}`}
            className="btn btn--secondary"
          >
            Dashboard
          </Link>
          <Link
            to={`/websites/${websiteId}/statement`}
            className="btn btn--secondary"
          >
            Statement
          </Link>
          <Link
            to={`/websites/${websiteId}/settings`}
            className="btn btn--secondary"
          >
            Scan settings
          </Link>
          <Link
            to={`/websites/${websiteId}/scan`}
            className="btn btn--secondary"
          >
            Run a new scan
          </Link>
        </div>
      </section>
    </div>
  );
}
